import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Translator } from './translator';
import { useLanguage } from "./LanguageContext";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('loading');
  const [secondsLeft, setSecondsLeft] = useState(null);

  const { translateText } = Translator();
  const { setLang } = useLanguage();

  const baseText = {
    header: "Reimposta la tua password",
    newPassword: "Nuova password",
    retypePassword: "Ripeti password",
    submit: "Invia",
    expired: "Questo link per reimpostare la password non è più valido.",
    success: "Password cambiata con successo. Reindirizzamento...",
    requestHeader: "Richiedi un nuovo link di reimpostazione",
    emailPlaceholder: "Inserisci la tua email",
    requestSent: "Un link per reimpostare la password è stato inviato alla tua email.",
    text1: "Utente non trovato",
    text2: "Errore interno. Riprova.",
    text3: "Errore di connessione",
    textOBL: "Email obbligatoria",
    invalidEmail: "Email non valida",
    textval: "Le password non corrispondono",
    tezt: "Qualcosa è andato storto. Riprova.",
    texterror: "Errore durante la richiesta.",
    textTT: "Entrambi i campi sono obbligatori",
    textCOR: "Le password non corrispondono",
    secondsSingular: "secondo",
    secondsPlural: "secondi",
  };

  const [translatedText, setTranslatedText] = useState(baseText);

  useEffect(() => {
    const translateAll = async () => {
      const entries = Object.entries(baseText);
      const translatedEntries = await Promise.all(
        entries.map(async ([key, value]) => [key, await translateText(value)])
      );
      setTranslatedText(Object.fromEntries(translatedEntries));
    };
    translateAll();
  }, [translateText]);

  useEffect(() => {
    const used = sessionStorage.getItem(`token-used-${token}`);
    if (token && !used) {
      setMode('reset');
    } else {
      setMode('request');
    }
  }, [token]);

  useEffect(() => {
    if (status === "requestSent") {
      setSecondsLeft(60);
      const interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            navigate("/login");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [status, navigate]);

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!pw1 || !pw2) return setError(translatedText.textTT);
    if (pw1 !== pw2) return setError(translatedText.textCOR);

    try {
      const res = await fetch(`https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/PWReset?token=${token}&pw1=${pw1}&pw2=${pw2}`);
      const result = await res.text();
      if (result === "1") {
        sessionStorage.setItem(`token-used-${token}`, "1");
        setStatus("success");
        setTimeout(() => navigate("/login"), 3000);
      } else if (["-2", "-3"].includes(result)) {
        setStatus("expired");
        setMode("request");
      } else if (result === "-1") {
        setError(translatedText.textval);
      } else {
        setError(translatedText.tezt);
      }
    } catch {
      setError(translatedText.texterror);
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!email) return setError(translatedText.textOBL);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError(translatedText.invalidEmail);

    try {
      const res = await fetch(`https://xmrhe4snn1.execute-api.eu-north-1.amazonaws.com/PWResetRequest?email=${email}`);
      const result = await res.text();
      if (result === "1") {
        setStatus("requestSent");
      } else if (result === "0") {
        setError(translatedText.text1);
      } else {
        setError(translatedText.text2);
      }
    } catch {
      setError(translatedText.text3);
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center min-h-screen bg-gray-100 p-6">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow text-center">
          <h2 className="text-green-600 text-xl font-semibold">{translatedText.success}</h2>
        </div>
      </div>
    );
  }

  if (status === "requestSent") {
    return (
      <div className="flex flex-col items-center min-h-screen bg-gray-100 p-6">
        <div className="max-w-md w-full bg-white p-6 rounded-lg shadow text-center">
          <h2 className="text-blue-600 text-lg font-medium">{translatedText.requestSent}</h2>
          {secondsLeft !== null && (
            <div className="mt-4 text-green-600 font-bold border-2 border-green-600 rounded-full px-4 py-2 inline-block">
              {secondsLeft} {secondsLeft === 1 ? translatedText.secondsSingular : translatedText.secondsPlural}...
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-100 p-6">
      <img src="/LOGO TECHE letters.png" alt="Logo" className="w-40 h-auto mb-4" />
      <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-md">
        {mode === "reset" ? (
          <>
            <h2 className="text-xl font-bold text-center text-black mb-4">{translatedText.header}</h2>
            <form onSubmit={handleResetSubmit} className="space-y-4">
              <div>
                <label className="block font-medium text-gray-700 mb-1">{translatedText.newPassword}</label>
                <input type="password" value={pw1} onChange={(e) => { setPw1(e.target.value); setError(''); }}
                  placeholder={translatedText.newPassword}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none" />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">{translatedText.retypePassword}</label>
                <input type="password" value={pw2} onChange={(e) => { setPw2(e.target.value); setError(''); }}
                  placeholder={translatedText.retypePassword}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none" />
              </div>
              {error && <p className="text-red-600 text-sm font-semibold">{error}</p>}
              <button type="submit" className="w-full bg-[#324B8E] text-white py-2 rounded-md hover:bg-[#273b6e] transition duration-200">
                {translatedText.submit}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold text-center text-black mb-4">{translatedText.requestHeader}</h2>
            <form onSubmit={handleRequestSubmit} className="space-y-4">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Email</label>
                <input type="text" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder={translatedText.emailPlaceholder}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none" />
              </div>
              {error && <p className="text-red-600 text-sm font-semibold">{error}</p>}
              <button type="submit" className="w-full bg-[#324B8E] text-white py-2 rounded-md hover:bg-[#273b6e] transition duration-200">
                {translatedText.submit}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
